import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useRTL } from '../../context/RTLContext';
import { Button, Input } from '../UI';
import { IconEye, IconEyeOff } from '@tabler/icons-react';

const LoginForm = ({ onSwitchToRegister, onLoginSuccess }) => {
  const { login, loading, error, clearError } = useAuth();
  const { t } = useTranslation();
  const { isRTL } = useRTL();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) {
      clearError();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Trim email + strip trailing whitespace from password — defends against
      // browser autofill that occasionally appends a space and breaks the
      // exact-match bcrypt compare. (Leading password whitespace is preserved
      // in case the user really meant it.)
      await login({
        email: formData.email.trim(),
        password: formData.password.replace(/\s+$/, ''),
      });
      if (onLoginSuccess) {
        onLoginSuccess();
      }
      navigate('/dashboard');
    } catch (error) {
      // Error is handled by AuthContext
      console.error('Login failed:', error.message);
    }
  };

  return (
    <div>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4 mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            {t('auth.login.email')}
          </label>
          <Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder={t('auth.login.emailPlaceholder')}
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
            {t('auth.login.password')}
          </label>
          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder={t('auth.login.passwordPlaceholder')}
              className={isRTL ? 'pl-10' : 'pr-10'}
            />
            <button
              type="button"
              className={`absolute top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 transition-colors ${isRTL ? 'left-1' : 'right-1'}`}
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <IconEyeOff size={18} />
              ) : (
                <IconEye size={18} />
              )}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="remember"
              name="remember"
              className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary focus:ring-2"
            />
            <label htmlFor="remember" className="ml-2 text-sm text-gray-600">
              {t('auth.login.rememberMe')}
            </label>
          </div>
          <button type="button" className="text-sm text-gray-700 hover:text-gray-900 transition-colors">
            {t('auth.login.forgotPassword')}
          </button>
        </div>

        <Button
          type="submit"
          disabled={loading}
          isLoading={loading}
          fullWidth
        >
          {loading ? t('common.status.loading') : t('auth.login.submit')}
        </Button>
      </form>

      <div className="mt-8 text-center">
        <p className="text-sm text-gray-600">
          {t('auth.login.noAccount')}{' '}
          <button
            onClick={onSwitchToRegister}
            className="text-gray-700 hover:text-gray-900 font-medium transition-colors"
          >
            {t('auth.login.signUpLink')}
          </button>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;